// Copyright (c) 2019-2023 Cigent Technology Inc. All Rights Reserved.
import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { NotificationService } from '../../../../services/notification-service';
import { UserService } from '../../../../services/user-service';
import { ISaveKeyDialogConfig, SaveKeyDialogComponent } from '../save-key-dialog/save-key-dialog.component';
import { TranslateService } from '@ngx-translate/core';
import { CustomValidators } from 'src/app/shared/validators/custom-validators';
import { finalize } from 'rxjs/operators';
import {UserRole} from '../../../../types/user';

@Component({
    selector: 'app-add-peer-user-dialog',
    templateUrl: './add-peer-user-dialog.component.html',
    styleUrls: ['./add-peer-user-dialog.component.scss'],
    standalone: false
})
export class AddPeerUserDialogComponent implements OnInit {
  registerForm: UntypedFormGroup;
  checkingEmailExists = false;
  selectedLanguage: string;

  fTitle: string;
  fFName: string;
  fLName: string;
  fEMail: string;
  fEMailExists: string;
  fEMailInvalid: string;
  fJob: string;
  fDepartment: string;
  fLocation: string;
  fRole: string;
  fAdmin: string;
  fUser: string;
  fGenerateKeys: string;
  fNewPass: string;
  fInvite: string;
  fCancel: string;
  fSave: string;
  fNew: string;

  readonly UserRole = UserRole;

  constructor(
    public dialogRef: MatDialogRef<AddPeerUserDialogComponent>,
    public dialog: MatDialog,
    private formBuilder: UntypedFormBuilder,
    private notificationService: NotificationService,
    private userService: UserService,
    private translate: TranslateService,
    @Inject(MAT_DIALOG_DATA) public data: any,
  ) {
    this.translate.get(['APU_TITLE', 'FIRST', 'LAST', 'APU_EMAIL_PHOLDER',
      'APU_EMAIL_EXISTS', 'APU_EMAIL_INVALID', 'APU_JOB_PHOLDER', 'APU_DEPARTMENT_PHOLDER',
      'APU_LOCATION_PHOLDER', 'APU_ROLE', 'APU_ADMIN', 'APU_USER', 'APU_GEN_KEYS',
      'APU_KEY_PASS', 'APU_INVITE', 'CANCEL_CAP', 'SAVE_CAP', 'APU_NEW']).subscribe(vals => {
        this.fTitle = vals['APU_TITLE'];
        this.fFName = vals['FIRST'];
        this.fLName = vals['LAST'];
        this.fEMail = vals['APU_EMAIL_PHOLDER'];
        this.fEMailExists = vals['APU_EMAIL_EXISTS'];
        this.fEMailInvalid = vals['APU_EMAIL_INVALID'];
        this.fJob = vals['APU_JOB_PHOLDER'];
        this.fDepartment = vals['APU_DEPARTMENT_PHOLDER'];
        this.fLocation = vals['APU_LOCATION_PHOLDER'];
        this.fRole = vals['APU_ROLE'];
        this.fAdmin = vals['APU_ADMIN'];
        this.fUser = vals['APU_USER'];
        this.fGenerateKeys = vals['APU_GEN_KEYS'];
        this.fNewPass = vals['APU_KEY_PASS'];
        this.fInvite = vals['APU_INVITE'];
        this.fCancel = vals['CANCEL_CAP'];
        this.fSave = vals['SAVE_CAP'];
        this.fNew = vals['APU_NEW'];
    });
    this.registerForm = this.formBuilder.group({
      fname: ['', [Validators.required, Validators.maxLength(255)]],
      lname: ['', [Validators.required, Validators.maxLength(255)]],
      email: ['', [Validators.required, Validators.pattern(CustomValidators.emailPattern),
        CustomValidators.maxByteLength(255)]],
      title: [''],
      department: [''],
      location: [''],
      generateKeys: [true],
      keyPassword: [''],
      role: [UserRole.USER, Validators.required],
      security_approver: [false],
      notifyApprovers: [false],
      sendEmail: [false],
    });
    this.registerForm.controls['notifyApprovers'].disable();
    if (this.registerForm.controls.generateKeys.value !== true) {
      this.registerForm.controls.keyPassword.disable();
    }
    this.selectedLanguage = localStorage.getItem('language') || 'en';
    if (this.data.disableApproverCheckbox) {
      this.registerForm.controls['security_approver'].disable();
    }
  }

  ngOnInit() {
  }

  onCancelClick() {
    this.dialogRef.close();
  }

  getValues() {
    const values = this.registerForm.value;
    values.email_notify_language = this.selectedLanguage;
    return values;
  }

  maybeSave(more: boolean) {
    const values = this.getValues();
    if (values.generateKeys && values.keyPassword) {
      const dialogConfig: ISaveKeyDialogConfig = {password: values.keyPassword};
      const saveDialogRef = this.dialog.open(SaveKeyDialogComponent, {
        width: '600px',
        height: 'auto',
        disableClose: true,
        data: dialogConfig,
      });
      saveDialogRef.afterClosed().subscribe(confirmed => {
        if (confirmed) {
          this.dialogRef.close({values: values, addMore: more});
        }
      });
    } else {
      this.dialogRef.close({values: values, addMore: more});
    }
  }

  onSubmit() {
    this.maybeSave(false);
  }

  onSubmitAndAddMore() {
    this.maybeSave(true);
  }

  get email() {
    return this.registerForm.controls['email'];
  }

  get doNotSendEmail() {
    return !this.registerForm.controls['sendEmail']?.value;
  }

  onEmailBlur() {
    if (this.isEmailFieldInvalid('email')) {
      // Email is invalid, don't check email yet!
    } else {
      if (this.registerForm.controls.email.errors) {
        return;
      }

      this.checkingEmailExists = true;
      this.userService.checkUserEmailExists(this.registerForm.value.email)
          .pipe(finalize(() => this.checkingEmailExists = false))
          .subscribe(
              next => {
                if (next.exists && next.verified) {
                  this.email.setErrors({'exists': true});
                }
              },
              () => {
                // Error state, probably email pattern
                this.email.setErrors({'pattern': true});
              }
          );
    }
  }

  changePasswordEnabled(event) {
    const control = this.registerForm.get('keyPassword');
    if (event.checked) {
      control.enable();
    } else {
      control.disable();
    }
  }

  isEmailFieldInvalid(field: string) {
    return (
      (!this.registerForm.get(field).valid && this.registerForm.get(field).touched) ||
      (this.registerForm.get(field).untouched)
    );
  }

  onLanguageChanged(language) {
    this.selectedLanguage = language;
  }

  approverChanged() {
    const notifyApprovers = this.registerForm.controls['notifyApprovers'];
    if (this.registerForm.controls['security_approver'].value) {
      notifyApprovers.enable();
      notifyApprovers.setValue(true);
    } else {
      notifyApprovers.disable();
      notifyApprovers.setValue(false);
    }
  }
}
